import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Alert,
  LinearProgress,
  Paper,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  AttachFile as AttachFileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Description as DocumentIcon,
} from '@mui/icons-material';

const FileUpload = ({ 
  files = [], 
  onFilesChange, 
  maxFiles = 5, 
  maxSize = 10 * 1024 * 1024, // 10MB
  acceptedTypes = [
    'image/jpeg',
    'image/jpg', 
    'image/png',
    'image/gif',
    'image/webp',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain',
    'text/csv'
  ],
  disabled = false 
}) => {
  const [dragOver, setDragOver] = useState(false);
  const [errors, setErrors] = useState([]);
  const fileInputRef = useRef(null);

  // Helper function to format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Helper function to get file icon
  const getFileIcon = (file) => {
    if (file.type.startsWith('image/')) {
      return <ImageIcon color="primary" />;
    } else if (file.type === 'application/pdf') {
      return <PdfIcon color="error" />;
    } else if (file.type.includes('document') || file.type.includes('word')) {
      return <DocumentIcon color="info" />;
    }
    return <AttachFileIcon />;
  };

  // Helper function to get file category chip
  const getFileCategory = (file) => {
    if (file.type.startsWith('image/')) {
      return <Chip label="Image" size="small" color="primary" />;
    } else if (file.type === 'application/pdf') {
      return <Chip label="PDF" size="small" color="error" />;
    } else if (file.type.includes('document') || file.type.includes('word')) {
      return <Chip label="Document" size="small" color="info" />;
    } else if (file.type.startsWith('text/')) {
      return <Chip label="Text" size="small" color="secondary" />;
    }
    return <Chip label="File" size="small" />;
  };

  // Validate files
  const validateFiles = (fileList) => {
    const newErrors = [];
    const validFiles = [];

    Array.from(fileList).forEach((file, index) => {
      // Check file count
      if (files.length + validFiles.length >= maxFiles) {
        newErrors.push(`Maximum ${maxFiles} files allowed`);
        return;
      }

      // Check file size
      if (file.size > maxSize) {
        newErrors.push(`${file.name} is too large (max ${formatFileSize(maxSize)})`);
        return;
      }

      // Check file type
      if (!acceptedTypes.includes(file.type)) {
        newErrors.push(`${file.name} has unsupported file type`);
        return;
      }

      // Check for duplicates
      const isDuplicate = files.some(existingFile => 
        existingFile.name === file.name && existingFile.size === file.size
      );
      
      if (isDuplicate) {
        newErrors.push(`${file.name} is already selected`);
        return;
      }

      validFiles.push(file);
    });

    setErrors(newErrors);
    return validFiles;
  };

  // Handle file selection
  const handleFileSelect = (selectedFiles) => {
    const validFiles = validateFiles(selectedFiles);
    if (validFiles.length > 0) {
      onFilesChange([...files, ...validFiles]);
    }
  };

  // Handle drag events
  const handleDragOver = (e) => {
    e.preventDefault();
    if (!disabled) {
      setDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    
    if (disabled) return;

    const droppedFiles = e.dataTransfer.files;
    handleFileSelect(droppedFiles);
  };

  // Handle file input change
  const handleFileInputChange = (e) => {
    const selectedFiles = e.target.files;
    handleFileSelect(selectedFiles);
    // Reset input value to allow selecting the same file again
    e.target.value = '';
  };

  // Remove file
  const removeFile = (index) => {
    const newFiles = files.filter((_, i) => i !== index);
    onFilesChange(newFiles);
    setErrors([]); // Clear errors when removing files
  };

  // Open file dialog
  const openFileDialog = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <Box>
      {/* File Drop Zone */}
      <Paper
        elevation={dragOver ? 4 : 1}
        sx={{
          p: 3,
          border: dragOver ? '2px dashed #1976d2' : '2px dashed #ccc',
          borderRadius: 2,
          textAlign: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          backgroundColor: dragOver ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
          opacity: disabled ? 0.6 : 1,
          transition: 'all 0.3s ease',
          '&:hover': {
            borderColor: disabled ? '#ccc' : '#1976d2',
            backgroundColor: disabled ? 'transparent' : 'rgba(25, 118, 210, 0.02)',
          },
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={openFileDialog}
      >
        <CloudUploadIcon 
          sx={{ 
            fontSize: 48, 
            color: dragOver ? 'primary.main' : 'text.secondary',
            mb: 2 
          }} 
        />
        <Typography variant="h6" gutterBottom>
          {dragOver ? 'Drop files here' : 'Upload Files'}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Drag and drop files here, or click to select files
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Max {maxFiles} files, {formatFileSize(maxSize)} each
        </Typography>
        <br />
        <Typography variant="caption" color="text.secondary">
          Supported: Images, PDFs, Documents
        </Typography>
        
        <Box mt={2}>
          <Button
            variant="outlined"
            startIcon={<AttachFileIcon />}
            disabled={disabled || files.length >= maxFiles}
            onClick={(e) => {
              e.stopPropagation();
              openFileDialog();
            }}
          >
            Choose Files
          </Button>
        </Box>
      </Paper>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={acceptedTypes.join(',')}
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
        disabled={disabled}
      />

      {/* Error messages */}
      {errors.length > 0 && (
        <Box mt={2}>
          {errors.map((error, index) => (
            <Alert key={index} severity="error" sx={{ mb: 1 }}>
              {error}
            </Alert>
          ))}
        </Box>
      )}

      {/* Selected files list */}
      {files.length > 0 && (
        <Box mt={2}>
          <Typography variant="subtitle2" gutterBottom>
            Selected Files ({files.length}/{maxFiles})
          </Typography>
          <List dense>
            {files.map((file, index) => (
              <ListItem key={index} divider>
                <Box sx={{ mr: 2 }}>
                  {getFileIcon(file)}
                </Box>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" noWrap>
                        {file.name}
                      </Typography>
                      {getFileCategory(file)}
                    </Box>
                  }
                  secondary={formatFileSize(file.size)}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => removeFile(index)}
                    disabled={disabled}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* Upload progress (if needed) */}
      {disabled && files.length > 0 && (
        <Box mt={2}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Uploading files...
          </Typography>
          <LinearProgress />
        </Box>
      )}
    </Box>
  );
};

export default FileUpload; 